import React, { Component } from "react";
import { Badge, Box, Button, CircularProgress, IconButton, Menu, Typography } from "@mui/material";
import QueueHeaderTooltip from "./QueueHeaderTooltip";
import { navigate } from "@reach/router";
import { withTranslation } from "react-i18next";
import { invalidateQueueCache, loadQueueCounts, loadQueueSummary } from "./queueApi";
import { emptyQueueSummary, filterQueueByLane, taskManagerHref, viewerFromUserData } from "./queueModel";
import { QueueWorkItemRow } from "./QueueWorkItemRow";
import { LANE_META, laneCount, laneById, laneHelpContent, laneTitle } from "./QueueLaneTiles";
import ExtensionIcon from "@mui/icons-material/Extension";

class WorkflowQueueButton extends Component {
  constructor(props) {
    super(props);
    this.state = {
      anchorEl: null,
      tab: "all",
      items: [],
      summary: emptyQueueSummary(),
      loading: false,
      countsLoaded: false,
    };
    this.pollId = null;
    this.bootId = null;
    this.handleOpen = this.handleOpen.bind(this);
    this.handleClose = this.handleClose.bind(this);
    this.handleOpenItem = this.handleOpenItem.bind(this);
    this.loadQueue = this.loadQueue.bind(this);
    this.openLane = this.openLane.bind(this);
  }

  componentDidMount() {
    var self = this;
    this.handleQueueSync = function () {
      invalidateQueueCache();
      self.loadQueue(true);
    };
    this.bootId = window.setTimeout(function () {
      self.loadQueue(false);
      self.pollId = window.setInterval(function () {
        self.loadQueue(false);
      }, 60000);
    }, 800);
    if (typeof window !== "undefined") {
      window.addEventListener("pickup-change-tasks-updated", this.handleQueueSync);
    }
  }

  componentDidUpdate(prevProps) {
    var prevId = prevProps.userData && prevProps.userData[0] && prevProps.userData[0].id;
    var nextId = this.props.userData && this.props.userData[0] && this.props.userData[0].id;
    if (prevId !== nextId) this.loadQueue(false);
  }

  componentWillUnmount() {
    if (this.bootId) window.clearTimeout(this.bootId);
    if (this.pollId) window.clearInterval(this.pollId);
    if (typeof window !== "undefined" && this.handleQueueSync) {
      window.removeEventListener("pickup-change-tasks-updated", this.handleQueueSync);
    }
  }

  loadQueue(full) {
    var self = this;
    var viewer = viewerFromUserData(this.props.userData);
    var loader = full
      ? loadQueueSummary(viewer, null, { force: true })
      : loadQueueCounts();
    loader
      .then(function (result) {
        var nextItems =
          result.items && result.items.length ? result.items : self.state.items;
        self.setState({
          items: full ? result.items || [] : nextItems,
          summary: result.summary,
          loading: false,
          countsLoaded: true,
        });
      })
      .catch(function () {
        self.setState({ loading: false, countsLoaded: true });
      });
  }

  handleOpen(event, lane) {
    this.setState({
      anchorEl: event.currentTarget,
      tab: lane || "all",
      loading: true,
    });
    this.loadQueue(true);
  }

  handleClose() {
    this.setState({ anchorEl: null });
  }

  handleOpenItem(item) {
    this.handleClose();
    navigate(taskManagerHref(item.id, item.type, item.lane));
  }

  openLane(lane) {
    this.handleClose();
    navigate(taskManagerHref("", "", lane));
  }

  render() {
    var t = this.props.t;
    var state = this.state;
    var open = Boolean(state.anchorEl);
    var selectedLane = laneById(state.tab);
    var visible =
      state.tab === "all"
        ? state.items.filter(function (item) {
            return item.lane !== "completed";
          })
        : filterQueueByLane(state.items, state.tab);
    var urgent = state.summary.urgent;
    var headerLanes = LANE_META.filter(function (lane) {
      return lane.id !== "completed";
    });

    return (
      <Box
        component="span"
        data-testid="workflow-queue"
        sx={{
          mr: 2,
          display: "inline-flex",
          alignItems: "center",
          alignSelf: "center",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            backgroundColor: "rgba(255,255,255,0.16)",
            borderRadius: "12px",
            px: 0.75,
            py: 0,
            my: 0.5,
            maxHeight: 44,
            boxSizing: "border-box",
          }}
        >
          {headerLanes.map(function (lane) {
            var Icon = lane.icon;
            var count = laneCount(state.summary, lane.id);
            var countsLoading = !state.countsLoaded;
            return (
              <QueueHeaderTooltip
                key={lane.id}
                wide
                title={laneHelpContent(lane, t)}
              >
                <IconButton
                  aria-label={laneTitle(lane, t)}
                  title=""
                  data-testid={"queue-header-" + lane.id}
                  onClick={function (event) { this.handleOpen(event, lane.id); }.bind(this)}
                  sx={{ color: "white", p: 0.5 }}
                >
                  {countsLoading ? (
                    <Box sx={{ position: "relative", width: 28, height: 28 }}>
                      <Icon sx={{ fontSize: 28, color: "white", opacity: 0.7 }} />
                      <CircularProgress
                        size={28}
                        thickness={4}
                        sx={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          color: "white",
                        }}
                      />
                    </Box>
                  ) : (
                    <Badge
                      badgeContent={count}
                      color="error"
                      max={99}
                      invisible={count === 0}
                      overlap="circular"
                      sx={{
                        "& .MuiBadge-badge": {
                          fontWeight: 700,
                          fontSize: 10,
                          minWidth: 18,
                          height: 18,
                        },
                      }}
                    >
                      <Icon sx={{ fontSize: 28, color: "white" }} />
                    </Badge>
                  )}
                </IconButton>
              </QueueHeaderTooltip>
            );
          }, this)}
          <Box
            sx={{
              width: "1px",
              alignSelf: "stretch",
              my: 0.75,
              backgroundColor: "rgba(255,255,255,0.35)",
            }}
          />
          <QueueHeaderTooltip
            title={t("Leftbar.Features", { defaultValue: "Features" })}
          >
            <IconButton
              aria-label={t("Leftbar.Features", { defaultValue: "Features" })}
              title=""
              data-testid="header-features"
              onClick={function () {
                navigate("/home/features");
              }}
              sx={{ color: "white", p: 0.5 }}
            >
              <ExtensionIcon sx={{ fontSize: 28, color: "white" }} />
            </IconButton>
          </QueueHeaderTooltip>
        </Box>
        <Menu
          id="menu-appbar-workflow-queue"
          anchorEl={state.anchorEl}
          open={open}
          onClose={this.handleClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          MenuListProps={{
            sx: {
              py: 0,
              display: "flex",
              flexDirection: "column",
              maxHeight: "calc(100vh - 88px)",
            },
          }}
          PaperProps={{
            elevation: 0,
            sx: {
              overflow: "hidden",
              width: 420,
              maxWidth: "92vw",
              maxHeight: "calc(100vh - 88px)",
              filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
              mt: 1,
            },
          }}
        >
          <Box sx={{ px: 2, pt: 1.5, pb: 1, flexShrink: 0 }}>
            <Typography sx={{ fontWeight: 700, color: "#0f172a" }}>
              {t("Queue.Title")}
            </Typography>
            <Typography sx={{ fontSize: 12, color: "#64748b" }}>
              {selectedLane
                ? laneTitle(selectedLane, t) +
                  (laneCount(state.summary, selectedLane.id)
                    ? " · " + laneCount(state.summary, selectedLane.id)
                    : "")
                : "Clear visibility. Actionable alerts. Nothing gets missed."}
            </Typography>
          </Box>
          {urgent > 0 ? (
            <Box
              data-testid="queue-urgent-strip"
              sx={{
                mx: 2,
                mb: 1,
                px: 1.5,
                py: 1,
                borderRadius: "8px",
                backgroundColor: "#fef2f2",
                border: "1px solid #fecaca",
                color: "#991b1b",
                fontSize: 13,
                fontWeight: 600,
                flexShrink: 0,
              }}
            >
              {t("Queue.UrgentStrip", { count: urgent })}
            </Box>
          ) : null}
          <Box
            sx={{
              px: 2,
              py: 1.5,
              flex: "1 1 auto",
              minHeight: 0,
              overflowY: "auto",
            }}
          >
            {state.loading && visible.length === 0 ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress size={24} />
              </Box>
            ) : visible.length === 0 ? (
              <Typography sx={{ color: "#94a3b8", textAlign: "center", py: 4, fontSize: 14 }}>
                {t("Queue.Empty")}
              </Typography>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {visible.map(function (item) {
                  return (
                    <QueueWorkItemRow
                      key={item.id}
                      item={item}
                      dense
                      onOpen={this.handleOpenItem}
                    />
                  );
                }, this)}
              </Box>
            )}
          </Box>
          <Box sx={{ px: 2, pb: 1.5, pt: 0.5, flexShrink: 0 }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={function () { this.openLane(state.tab === "all" ? "" : state.tab); }.bind(this)}
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              {t("Queue.OpenTaskManager")}
            </Button>
          </Box>
        </Menu>
      </Box>
    );
  }
}

export default withTranslation()(WorkflowQueueButton);



// WEBPACK FOOTER //
// ./src/components/workflowQueue/WorkflowQueueButton.js